import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type SurveyReportResult, type SurveySettingResult } from '@whinerp/client';
import { formatPercent } from '@whinerp/contracts/format';
import { Panel, SectionLabel } from '@whinerp/ui';
import { useFormat, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';

/**
 * How customers rated resolved cases (G-197), with the measure in use - and,
 * for somebody who may change it, the choice of measure.
 */
export function SurveyPanel(): JSX.Element | null {
  const t = useT();
  const format = useFormat();
  const queryClient = useQueryClient();
  const { can } = useSession();
  const setting = useQuery<SurveySettingResult, ApiError>({ queryKey: ['survey-setting'], queryFn: () => api.surveySetting({}) });
  const report = useQuery<SurveyReportResult, ApiError>({ queryKey: ['survey-report'], queryFn: () => api.surveyReport({ query: {} }) });
  const save = useMutation({
    mutationFn: (next: SurveySettingResult) => api.saveSurveySetting({ body: next }),
    onSuccess: (result) => {
      queryClient.setQueryData(['survey-setting'], result);
      void queryClient.invalidateQueries({ queryKey: ['survey-report'] });
    },
  });
  if (!setting.data || !report.data) return null;
  const view = report.data;
  const shown = (score: number | null) =>
    score === null ? '-' : view.kind === 'csat' ? formatPercent(score, format) : view.kind === 'nps' ? String(score) : `${String(score)} / 7`;
  return (
    <Panel data-region="surveys">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <SectionLabel>{t('survey.panel')}</SectionLabel>
          {can('setup.schema.manage') ? (
            <select className="ms-auto h-input rounded-control border border-control bg-surface px-2 text-body text-ink" data-survey-kind
              aria-label={t('survey.measure')} value={setting.data.active ? setting.data.kind : 'off'}
              onChange={(event) => save.mutate(event.target.value === 'off' ? { ...setting.data, active: false } : { kind: event.target.value as 'csat', active: true })}>
              <option value="off">{t('survey.off')}</option>
              {(['csat', 'nps', 'ces'] as const).map((kind) => <option key={kind} value={kind}>{t(`survey.kind.${kind}`)}</option>)}
            </select>
          ) : null}
        </div>
        <p className="text-dense text-ink" data-survey-score>
          {t('survey.summary', { values: { measure: t(`survey.kind.${view.kind}`), score: shown(view.score), answered: String(view.answered), sent: String(view.sent) } })}
        </p>
        {view.byAgent.length > 0 ? (
          <ul className="flex flex-col gap-1 text-dense" data-survey-agents>
            {view.byAgent.map((agent) => (
              <li key={agent.agentId ?? 'none'} className="flex gap-2">
                <span className="text-ink">{agent.agentName ?? t('survey.unassigned')}</span>
                <span className="ms-auto text-ink-600">{shown(agent.score)} ({agent.answered})</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </Panel>
  );
}
